import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";
import { authApi } from "@/services/auth";
import { useSetAtom } from "jotai";
import { authStateAtom } from "@/stores/auth.atom";

export default function AuthCallback() {
  const navigate = useNavigate();
  const setAuthState = useSetAtom(authStateAtom);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (isProcessing) return; // Prevent multiple executions

      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const error = params.get("error");

      // Handle OAuth error
      if (error) {
        toast({
          variant: "destructive",
          title: "Đăng nhập thất bại",
          description:
            "Có lỗi xảy ra trong quá trình xác thực. Vui lòng thử lại.",
        });
        navigate("/login");
        return;
      }

      // Handle missing authorization code
      if (!code) {
        toast({
          variant: "destructive",
          title: "Đăng nhập thất bại",
          description: "Không nhận được mã xác thực. Vui lòng thử lại.",
        });
        navigate("/login");
        return;
      }

      setIsProcessing(true);

      try {
        // Exchange authorization code for tokens using backend API
        const response = await authApi.googleAuth(code);

        if (response.access_token && response.user) {
          // Store tokens
          localStorage.setItem("access_token", response.access_token);
          localStorage.setItem("refresh_token", response.refresh_token);

          // Convert to our User type format
          const user = {
            id: response.user.id,
            email: response.user.email,
            first_name: response.user.first_name,
            last_name: response.user.last_name,
            display_name:
              `${response.user.first_name} ${response.user.last_name}`.trim(),
            avatar_url: response.user.avatar_url || undefined,
            bio: "",
            is_staff: false,
            is_verified: false,
            joined_at: new Date().toISOString(),
            last_login: response.last_login,
            post_count: 0,
            communities: [],
            location: undefined,
            username: response.user.email,
          };

          // Update auth state
          setAuthState({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Check if user has invitation code stored
          const invitationCode = localStorage.getItem("invitation_code");
          if (invitationCode) {
            localStorage.removeItem("invitation_code");
            navigate("/location");
          } else {
            navigate("/location");
          }

          toast({
            title: "Đăng nhập thành công",
            description: `Chào mừng ${user.display_name} đến với Locuno!`,
          });
        } else {
          throw new Error("Invalid response from Google authentication");
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Có lỗi xảy ra trong quá trình đăng nhập";

        toast({
          variant: "destructive",
          title: "Đăng nhập thất bại",
          description: errorMessage,
        });

        navigate("/login");
      } finally {
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [navigate, setAuthState, isProcessing]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center bg-white p-8 rounded-lg shadow-md">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Đang xử lý đăng nhập...</p>
        <p className="mt-2 text-sm text-gray-500">
          Vui lòng không đóng trang này
        </p>
      </div>
    </div>
  );
}
